/**
 * LineOARow — LINE OA sparkline tiles for the dashboard
 *
 * Renders PulseTile-style sparkline cards for Followers and Targeted Reach,
 * plus a New This Month tile. Shares the global PeriodSelector via preferencesStore.
 * Hidden when LINE Messaging API is not connected.
 */

import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Settings, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { AreaChart, Area, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useLineMessagingConfig } from "@/features/storefront/settings/useLineMessagingConfig";
import { useLineOADashboard } from "@/features/line-oa/useLineOADashboard";
import { useLineOASparkline } from "@/features/line-oa/useLineOASparkline";
import { cpGetLineOASparkline } from "@/lib/api/controlPlaneClient";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import type { PulsePeriod } from "@/hooks/usePulseSparkline";

const ALL_PERIODS: PulsePeriod[] = ["7d", "mtd", "30d", "90d"];

// ── Formatting helpers ──────────────────────────────────

function formatValue(value: number | null | undefined): string {
  if (value == null) return "—";
  if (value >= 1_000_000) {
    return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(value);
  }
  return new Intl.NumberFormat("en").format(value);
}

const PERIOD_LABELS: Record<string, string> = { "7d": "7 days", "mtd": "this month", "30d": "30 days", "90d": "90 days" };

// ── Count-up hook (matching PulseTile) ──────────────────

function useCountUp(target: number, duration = 400): number {
  const reducedMotion = useReducedMotion();
  const [current, setCurrent] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (reducedMotion || target === 0) { setCurrent(target); return; }
    setCurrent(0);
    const start = performance.now();
    function animate(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    }
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); };
  }, [target, duration, reducedMotion]);

  return current;
}

// ── Sparkline tooltip ───────────────────────────────────

const canHover = typeof window !== "undefined" && window.matchMedia("(hover: hover)").matches;

function SparklineTooltipContent({ active, payload }: { active?: boolean; payload?: Array<{ value?: number }> }) {
  if (!canHover || !active || payload?.[0]?.value == null) return null;
  return (
    <div className="bg-foreground text-background text-[10px] px-1.5 py-0.5 rounded-md shadow-sm whitespace-nowrap">
      {payload[0].value.toLocaleString()}
    </div>
  );
}

// ── LINE OA Tile ────────────────────────────────────────

interface LineOATileProps {
  label: string;
  value: number | null | undefined;
  href: string;
  isLoading?: boolean;
  sparklineData?: Array<{ date: string; value: number }>;
  isSparklineLoading?: boolean;
  delta?: number | null;
  deltaPct?: number | null;
  avg?: number | null;
  period?: PulsePeriod;
  color: string;
  gradientId: string;
}

function LineOATile({
  label, value, href, isLoading, sparklineData, isSparklineLoading,
  delta, deltaPct, avg, period, color, gradientId,
}: LineOATileProps) {
  const reducedMotion = useReducedMotion();
  const animatedValue = useCountUp(isLoading ? 0 : (value ?? 0));

  if (isLoading) {
    return (
      <div className={cn(
        "flex-1 min-w-0 rounded-xl p-4",
        "bg-card",
        "border border-border"
      )} aria-hidden="true">
        <div className="flex flex-col justify-between min-h-[160px]">
          <div className="h-3 w-16 bg-muted rounded-md animate-pulse" />
          <div>
            <div className="h-7 w-12 bg-muted rounded-md animate-pulse mb-2" />
            <div className="h-3 w-24 bg-muted rounded-md animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  // Auto-scale Y axis so cumulative counts (followers, reach) don't max out
  const spValues = (sparklineData ?? []).map((d) => d.value);
  let lineYMin = 0;
  let lineYMax: number | string = "dataMax";
  if (spValues.length > 0) {
    const rawMin = Math.min(...spValues);
    const rawMax = Math.max(...spValues);
    const range = rawMax - rawMin;
    const pad = range > 0 ? range * 0.15 : rawMax * 0.1 || 1;
    lineYMin = Math.max(0, Math.floor(rawMin - pad));
    lineYMax = Math.ceil(rawMax + pad);
  }

  const isZero = value === 0 || value == null;
  const periodLabel = period ? PERIOD_LABELS[period] ?? period : "";
  const hasDelta = delta != null && delta !== 0;
  const isUp = (delta ?? 0) > 0;

  return (
    <Link
      to={href}
      className={cn(
        "relative flex-1 min-w-0 rounded-xl p-4 overflow-hidden",
        "bg-card",
        "border border-border",
        "transition-all duration-200",
        "hover:scale-[1.02] hover:shadow-apple-sm active:scale-[0.98]"
      )}
      aria-label={`${value ?? 0} ${label}`}
    >
      <div className="relative z-10 flex flex-col min-h-[160px]">
        <p className="text-[11px] font-medium uppercase tracking-wider truncate text-muted-foreground">
          {label}
        </p>
        <p className={cn(
          "text-3xl font-semibold leading-tight mt-1.5",
          isZero ? "text-muted-foreground" : "text-foreground"
        )}>
          {formatValue(animatedValue)}
        </p>

        {/* Period comparison */}
        {period && (
          <div className="mt-1 space-y-0.5">
            {isZero ? (
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground/50">
                <Minus className="w-2.5 h-2.5" />
                <span>vs last {periodLabel}</span>
              </div>
            ) : hasDelta ? (
              <div className={cn(
                "flex items-center gap-1 text-[10px] font-medium",
                isUp ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"
              )}>
                {isUp ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                <span>
                  {deltaPct != null ? `${isUp ? "+" : ""}${deltaPct}%` : `${isUp ? "+" : ""}${delta}`}
                  {" "}vs last {periodLabel}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <Minus className="w-2.5 h-2.5" />
                <span>0% vs last {periodLabel}</span>
              </div>
            )}
            {avg != null && (
              <p className="text-[10px] text-muted-foreground">
                ~{formatValue(Math.round(avg))}/day growth
              </p>
            )}
          </div>
        )}
      </div>

      {/* Sparkline background */}
      {!isSparklineLoading && sparklineData && sparklineData.length > 0 && (
        <div className={cn("absolute bottom-0 left-0 right-0 h-[55%]", !reducedMotion && "transition-opacity duration-200")}>
          <div className="h-full w-full" role="img" aria-label={`Trend: ${sparklineData.map(d => d.value).join(", ")}`}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparklineData} margin={{ top: 0, right: 0, bottom: 6, left: 0 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <YAxis domain={[lineYMin, lineYMax]} hide />
                <Tooltip
                  content={<SparklineTooltipContent />}
                  cursor={{ stroke: color, strokeWidth: 1 }}
                  wrapperStyle={{ zIndex: 10 }}
                  position={{ y: -28 }}
                  isAnimationActive={false}
                />
                <Area
                  type="linear"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={1.5}
                  fill={`url(#${gradientId})`}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Link>
  );
}

// ── Main Row ────────────────────────────────────────────

export function LineOARow() {
  const { status, isLoading: isLoadingStatus } = useLineMessagingConfig();
  const isConnected = !!status?.connected;
  const { data: dashData, isLoading: isLoadingDash } = useLineOADashboard({ enabled: isConnected });
  const pulsePeriod = usePreferencesStore((s) => s.pulsePeriod);
  const { data: sparkData, isLoading: isSparkLoading } = useLineOASparkline(pulsePeriod, isConnected && !isLoadingDash);

  // Prefetch all other periods in background so switching is instant
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!isConnected || isLoadingDash) return;
    for (const p of ALL_PERIODS) {
      if (p !== pulsePeriod) {
        queryClient.prefetchQuery({
          queryKey: ["line-oa-sparkline", p],
          queryFn: () => cpGetLineOASparkline(p),
          staleTime: 10 * 60 * 1000,
        });
      }
    }
  }, [isConnected, isLoadingDash, pulsePeriod, queryClient]);

  if (isLoadingStatus) return null;

  // Not connected — compact connect prompt
  if (!isConnected) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.25 }}
        className={cn(
          "rounded-xl p-5",
          "bg-card",
          "border border-border"
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-sm bg-[#06C755] flex items-center justify-center">
              <span className="text-white text-xs font-bold">L</span>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                LINE Official Account
              </h2>
              <p className="text-xs text-muted-foreground">
                Connect to see followers, send broadcasts, and manage rich menus
              </p>
            </div>
          </div>
          <Link
            to="/storefront/settings"
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium",
              "bg-[#06C755] text-white hover:bg-[#05b34d] transition-colors"
            )}
          >
            <Settings className="w-3.5 h-3.5" />
            Connect
          </Link>
        </div>
      </motion.div>
    );
  }

  // Connected — sparkline tiles
  const isLoading = isLoadingDash && !dashData;
  const summary = sparkData?.summary;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.25 }}
      className="space-y-2"
    >
      {/* Section header */}
      <Link to="/line-oa" className="flex items-center gap-2 group px-1">
        <div className="w-5 h-5 rounded-md bg-[#06C755] flex items-center justify-center">
          <span className="text-white text-[9px] font-bold">L</span>
        </div>
        <h2 className="text-sm font-semibold text-foreground">
          LINE OA
        </h2>
      </Link>

      {/* Tiles */}
      <div className="flex gap-4 w-full">
        <LineOATile
          label="Followers"
          value={dashData?.followers}
          href="/line-oa/audience"
          isLoading={isLoading}
          sparklineData={sparkData?.followers}
          isSparklineLoading={isSparkLoading}
          delta={summary?.followers_delta}
          deltaPct={summary?.followers_delta_pct}
          avg={summary?.followers_avg}
          period={pulsePeriod}
          color="#06C755"
          gradientId="line-sparkline-followers"
        />
        <LineOATile
          label="Targeted Reach"
          value={dashData?.targeted_reach}
          href="/line-oa"
          isLoading={isLoading}
          sparklineData={sparkData?.targeted_reach}
          isSparklineLoading={isSparkLoading}
          delta={summary?.reach_delta}
          deltaPct={summary?.reach_delta_pct}
          avg={summary?.reach_avg}
          period={pulsePeriod}
          color="#4A90D9"
          gradientId="line-sparkline-reach"
        />
        <LineOATile
          label="New Followers"
          value={summary?.new_total ?? dashData?.new_this_month}
          href="/line-oa"
          isLoading={isLoading}
          sparklineData={sparkData?.new_followers}
          isSparklineLoading={isSparkLoading}
          delta={summary?.new_delta}
          deltaPct={summary?.new_delta_pct}
          avg={summary?.new_avg}
          period={pulsePeriod}
          color="#9B59B6"
          gradientId="line-sparkline-new"
        />
      </div>
    </motion.div>
  );
}
